import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  FaBars,
  FaSearch,
  FaBell,
  FaChevronDown,
  FaUserCircle,
  FaUser,
  FaKey,
  FaSignOutAlt,
} from "react-icons/fa";

import "./Topbar.css";
import { BASE_URL } from "../../Config/config";

export default function Topbar({ onMenuClick }) {
  const navigate = useNavigate();

  const [inquiryCount, setInquiryCount] = useState(0);
  const [loadingNotifications, setLoadingNotifications] = useState(false);

  const [adminName, setAdminName] = useState(
    sessionStorage.getItem("admin_name") || "Administrator",
  );

  // Profile dropdown
  const [showProfileMenu, setShowProfileMenu] = useState(false);

  const profileRef = useRef(null);

  /* =========================================================
     FETCH NEW INQUIRY COUNT
  ========================================================= */

  const fetchInquiryCount = useCallback(async () => {
    try {
      const response = await fetch(
        `${BASE_URL}/dashboard.php?action=get_new_inquiries_count`,
      );

      if (!response.ok) {
        throw new Error(`HTTP Error: ${response.status}`);
      }

      const result = await response.json();

      console.log("New Inquiry Count:", result);

      if (result.status) {
        setInquiryCount(Number(result.data?.count || 0));
      }
    } catch (error) {
      console.error("Inquiry notification error:", error);
    }
  }, []);
  useEffect(() => {
    const updateAdminName = () => {
      setAdminName(sessionStorage.getItem("admin_name") || "Administrator");
    };

    window.addEventListener("admin-profile-updated", updateAdminName);

    return () => {
      window.removeEventListener("admin-profile-updated", updateAdminName);
    };
  }, []);
  /* =========================================================
     INITIAL LOAD + AUTO REFRESH
  ========================================================= */

  useEffect(() => {
    fetchInquiryCount();

    const interval = setInterval(() => {
      fetchInquiryCount();
    }, 10000);

    return () => {
      clearInterval(interval);
    };
  }, [fetchInquiryCount]);

  /* =========================================================
     CLOSE PROFILE DROPDOWN WHEN CLICKING OUTSIDE
  ========================================================= */

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setShowProfileMenu(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  /* =========================================================
     MARK INQUIRIES AS SEEN
  ========================================================= */

  const handleNotificationClick = async () => {
    try {
      setLoadingNotifications(true);

      // If there are no notifications, still go to inquiry page
      if (inquiryCount === 0) {
        navigate("/MyAdminStar/inquiry");
        return;
      }

      const response = await fetch(
        `${BASE_URL}/dashboard.php?action=mark_inquiries_seen`,
        {
          method: "POST",
        },
      );

      if (!response.ok) {
        throw new Error(`HTTP Error: ${response.status}`);
      }

      const result = await response.json();

      console.log("Mark inquiries seen:", result);

      // Clear notification count
      setInquiryCount(0);

      // ALWAYS redirect after clicking notification
      navigate("/MyAdminStar/inquiry");
    } catch (error) {
      console.error("Mark inquiry error:", error);

      // Even if marking as seen fails,
      // still open inquiry page
      navigate("/MyAdminStar/inquiry");
    } finally {
      setLoadingNotifications(false);
    }
  };

  /* =========================================================
     PROFILE MENU
  ========================================================= */

  const handleProfileClick = () => {
    setShowProfileMenu((prev) => !prev);
  };

  /* =========================================================
     PROFILE
  ========================================================= */

  const handleProfile = () => {
    setShowProfileMenu(false);

    // Change this route according to your application
    window.location.href = "/MyAdminStar/profile";
  };

  /* =========================================================
     CHANGE PASSWORD
  ========================================================= */

  const handleChangePassword = () => {
    setShowProfileMenu(false);

    // Change this route according to your application
    window.location.href = "/MyAdminStar/change-password";
  };

  /* =========================================================
     LOGOUT
  ========================================================= */

  const handleLogout = () => {
    setShowProfileMenu(false);

    const confirmLogout = window.confirm("Are you sure you want to logout?");

    if (!confirmLogout) {
      return;
    }

    /*
     * Remove your login/session values here.
     * Add/remove keys according to your login implementation.
     */
    localStorage.removeItem("admin");
    localStorage.removeItem("admin_id");
    localStorage.removeItem("user_id");
    localStorage.removeItem("token");
    localStorage.removeItem("jwt_token");

    sessionStorage.removeItem("admin");
    sessionStorage.removeItem("admin_id");
    sessionStorage.removeItem("user_id");
    sessionStorage.removeItem("token");
    sessionStorage.removeItem("jwt_token");

    // Redirect to login
    window.location.href = "/MyAdminStar/login";
  };

  return (
    <header className="topbar">
      {/* =====================================================
          LEFT
      ===================================================== */}

      <div className="topbar-left">
        <button className="menu-btn" onClick={onMenuClick}>
          <FaBars />
        </button>

        <div className="page-heading">
          <h1>Dashboard</h1>
          <span>Welcome back, {adminName}</span>
        </div>
      </div>

      {/* =====================================================
          RIGHT
      ===================================================== */}

      <div className="topbar-right">
        {/* ===================================================
            SEARCH
        =================================================== */}

        <div className="topbar-search">
          <FaSearch />

          <input type="text" placeholder="Search here..." />
        </div>

        {/* ===================================================
            NOTIFICATION
        =================================================== */}

        <button
          className={`notification-btn ${
            inquiryCount > 0 ? "has-notifications" : ""
          }`}
          onClick={handleNotificationClick}
          title={
            inquiryCount > 0
              ? `${inquiryCount} new ${
                  inquiryCount === 1 ? "inquiry" : "inquiries"
                }`
              : "No new inquiries"
          }
          disabled={loadingNotifications}
        >
          <FaBell />

          {inquiryCount > 0 && (
            <span className="notification-count">
              {inquiryCount > 99 ? "99+" : inquiryCount}
            </span>
          )}
        </button>

        {/* ===================================================
            DIVIDER
        =================================================== */}

        <div className="topbar-divider"></div>

        {/* ===================================================
            PROFILE
        =================================================== */}

        <div className="topbar-profile-wrapper" ref={profileRef}>
          <div className="topbar-profile" onClick={handleProfileClick}>
            <div className="topbar-avatar">
              <FaUserCircle />
            </div>

            <div className="topbar-user">
              <strong>{adminName}</strong>
              <span>Super Admin</span>
            </div>

            <FaChevronDown
              className={`profile-arrow ${
                showProfileMenu ? "profile-arrow-open" : ""
              }`}
            />
          </div>

          {/* =================================================
              PROFILE DROPDOWN
          ================================================= */}

          {showProfileMenu && (
            <div className="profile-dropdown">
              <button className="profile-dropdown-item" onClick={handleProfile}>
                <span className="dropdown-icon">
                  <FaUser />
                </span>

                <span>
                  <strong>Profile</strong>
                  <small>View your profile</small>
                </span>
              </button>

              <button
                className="profile-dropdown-item"
                onClick={handleChangePassword}
              >
                <span className="dropdown-icon">
                  <FaKey />
                </span>

                <span>
                  <strong>Change Password</strong>
                  <small>Update your password</small>
                </span>
              </button>

              <div className="dropdown-divider"></div>

              {/* Logout */}

              <button
                className="profile-dropdown-item logout-item"
                onClick={handleLogout}
              >
                <span className="dropdown-icon">
                  <FaSignOutAlt />
                </span>

                <span>
                  <strong>Logout</strong>
                  <small>Sign out of your account</small>
                </span>
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
